import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ExternalLink, FileText, Loader2, UploadCloud, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import { AuthImage, open_file_in_new_tab } from '@/components/auth-image';
import { Label } from '@/components/ui/label';
import { SelectField } from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import {
  use_draft,
  use_my_kyc_docs,
  use_my_photos,
  use_upload_kyc,
  use_upload_photo,
} from '@/features/onboarding/hooks';
import {
  module_steps_for_draft,
  module_path,
} from '@/features/onboarding/module-step-router';
import {
  KYC_DOCUMENT_TYPES,
  PHOTO_TYPES,
  PHOTO_TYPE_LABELS,
  type KycDocumentType,
  type VendorPhotoType,
} from '@/features/onboarding/types';

const KYC_ACCEPT = 'application/pdf,image/jpeg,image/png';
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

const KYC_MAX_BYTES = 10 * 1024 * 1024;
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
/** Same cap as the backend (client change, 9 Oct 2026): up to 5 photos per tag. */
const MAX_PHOTOS_PER_TAG = 5;

/**
 * Pre-flight the same rules the API enforces. `accept` only filters the file
 * picker — drag-drop and "All files" still get through — and without this the
 * user waits out a full 10 MB upload just to be told "MIME not allowed:
 * text/plain" in the backend's wording.
 */
function file_rejection_reason(
  file: File,
  accept: string,
  max_bytes: number,
  human_types: string,
): string | null {
  const allowed = accept.split(',');
  if (!allowed.includes(file.type)) {
    return `“${file.name}” is not a supported file type. Please pick a ${human_types} file.`;
  }
  if (file.size > max_bytes) {
    return `“${file.name}” is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is ${Math.round(max_bytes / (1024 * 1024))} MB.`;
  }
  return null;
}

export function UploadsPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const upload_kyc_m = use_upload_kyc();
  const upload_photo_m = use_upload_photo();
  // Server-listed uploads: pre-populates on revisit/refresh — uploads from
  // earlier sessions with this phone show up too.
  const kyc_docs_q = use_my_kyc_docs({ enabled: !!draft_q.data });
  const photos_q = use_my_photos({ enabled: !!draft_q.data });

  const [kyc_type, set_kyc_type] = useState<KycDocumentType>('OWNER_ID_PROOF');
  const [photo_type, set_photo_type] = useState<VendorPhotoType>('STORE_FRONT');
  const [error, set_error] = useState<string | null>(null);
  const kyc_input_ref = useRef<HTMLInputElement>(null);
  const photo_input_ref = useRef<HTMLInputElement>(null);

  const kyc_docs = kyc_docs_q.data ?? [];
  const photos = photos_q.data ?? [];
  /*
   * A profile photo is mandatory for the at-your-door modules (walker, trainer): the vendor
   * IS the person who turns up, and a customer is entitled to a face before handing over
   * their dog. Gated here rather than only at submit so the vendor fixes it on the screen
   * that uploads photos, instead of being bounced back from the last step.
   */
  const needs_profile_photo = (draft_q.data?.module_types ?? []).some(
    (m) => m === 'WALKER' || m === 'TRAINER',
  );
  const has_profile_photo = photos.some((p) => p.type === 'PROFILE_PHOTO');
  const profile_photo_missing = needs_profile_photo && !has_profile_photo;
  const tag_count = photos.filter((p) => p.type === photo_type).length;
  const tag_room = Math.max(0, MAX_PHOTOS_PER_TAG - tag_count);
  const tag_label = PHOTO_TYPE_LABELS[photo_type] ?? photo_type;
  // Client change (9 Oct 2026): vets must evidence their degree. The server enforces it at submit.
  const needs_degree_cert = (draft_q.data?.module_types ?? []).includes('VET');
  const has_degree_cert = kyc_docs.some((d) => d.type === 'DEGREE_CERTIFICATE');
  const degree_cert_missing = needs_degree_cert && !has_degree_cert;

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  async function handle_kyc_select(e: React.ChangeEvent<HTMLInputElement>) {
    set_error(null);
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;
    const rejected = file_rejection_reason(file, KYC_ACCEPT, KYC_MAX_BYTES, 'PDF, JPG or PNG');
    if (rejected) {
      set_error(rejected);
      return;
    }
    try {
      // List refetches via mutation's invalidate — no local mirror needed.
      await upload_kyc_m.mutateAsync({ type: kyc_type, file });
    } catch (err) {
      set_error(err instanceof ApiError ? err.message : 'KYC upload failed');
    }
  }

  /*
   * Client change (9 Oct 2026): pick a tag, then select up to MAX_PHOTOS_PER_TAG photos of it
   * in one go. The server enforces the same cap; checking here first tells the vendor before
   * anything uploads.
   */
  async function handle_photo_select(e: React.ChangeEvent<HTMLInputElement>) {
    set_error(null);
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    if (files.length > tag_room) {
      set_error(
        tag_room === 0
          ? `You already have ${MAX_PHOTOS_PER_TAG} ${tag_label} photos — remove one to add another.`
          : `You can add ${tag_room} more ${tag_label} photo${tag_room === 1 ? '' : 's'} (max ${MAX_PHOTOS_PER_TAG} per tag).`,
      );
      return;
    }
    for (const file of files) {
      const rejected = file_rejection_reason(file, PHOTO_ACCEPT, PHOTO_MAX_BYTES, 'JPG, PNG or WEBP');
      if (rejected) {
        set_error(`${file.name}: ${rejected}`);
        return;
      }
    }
    // One at a time so a failure stops cleanly and names the file.
    for (const file of files) {
      try {
        await upload_photo_m.mutateAsync({ type: photo_type, file });
      } catch (err) {
        set_error(`${file.name}: ${err instanceof ApiError ? err.message : 'Photo upload failed'}`);
        return;
      }
    }
  }

  async function handle_view(path: string) {
    set_error(null);
    try {
      await open_file_in_new_tab(path);
    } catch {
      set_error('Could not open the file. If the backend restarted since upload (dev stub storage), re-upload it.');
    }
  }

  function format_bytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <StepSection
      step_key="uploads"
      title="Documents & photos"
      description="Uploads are claimed to your vendor profile when you submit at the end. Skip any you don't have — admin will request what's missing."
      error={error}
      footer={
        <Button
          variant="gradient"
          disabled={profile_photo_missing || degree_cert_missing}
          onClick={() => {
            if (degree_cert_missing) {
              set_error('Upload your degree certificate — it is required for vets.');
              return;
            }
            if (profile_photo_missing) {
              set_error('Add a profile photo of yourself — it is required for walkers and trainers.');
              return;
            }
            const next = module_steps_for_draft(draft_q.data);
            navigate(next[0] ? module_path(next[0]) : '/onboarding/payout');
          }}
          data-testid="uploads-continue"
        >
          Continue
        </Button>
      }
    >
      <section className="rounded-lg border border-border bg-card/60 p-4">
        <h3 className="mb-3 font-heading text-base font-semibold">KYC documents</h3>
        {needs_degree_cert && (
          <p
            className={
              'mb-3 rounded-md border px-3 py-2 text-sm ' +
              (has_degree_cert
                ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
                : 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400')
            }
            data-testid="degree-cert-requirement"
            data-satisfied={has_degree_cert}
          >
            {has_degree_cert
              ? 'Degree certificate added.'
              : 'Your degree certificate is required to register as a vet. Pick “DEGREE_CERTIFICATE” below.'}
          </p>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="kyc-type">Document type</Label>
            <SelectField
              id="kyc-type"
              data-testid="kyc-type"
              value={kyc_type}
              onValueChange={(v) => set_kyc_type(v as KycDocumentType)}
              options={KYC_DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => kyc_input_ref.current?.click()}
            disabled={upload_kyc_m.isPending}
            data-testid="kyc-pick"
          >
            {upload_kyc_m.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="h-4 w-4" />
            )}
            Pick file (PDF/JPG/PNG, ≤ 10 MB)
          </Button>
          <input
            ref={kyc_input_ref}
            type="file"
            data-testid="kyc-file-input"
            accept={KYC_ACCEPT}
            onChange={handle_kyc_select}
            className="hidden"
          />
        </div>
        {kyc_docs.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="kyc-empty">
            No documents uploaded yet.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-border">
            {kyc_docs.map((d) => (
              <li
                key={d.id}
                data-testid={`kyc-uploaded-${d.id}`}
                className="flex items-center gap-3 py-2 text-sm"
              >
                {d.mime_type.startsWith('image/') ? (
                  <AuthImage
                    path={`/vendor/uploads/kyc-docs/${d.id}/file`}
                    alt={d.original_filename}
                    className="h-12 w-12 shrink-0 rounded-md border border-border object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{d.type}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {d.original_filename} · {format_bytes(d.size_bytes)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handle_view(`/vendor/uploads/kyc-docs/${d.id}/file`)}
                  data-testid={`kyc-view-${d.id}`}
                  aria-label={`View ${d.original_filename}`}
                >
                  <ExternalLink className="h-4 w-4" /> View
                </Button>
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card/60 p-4">
        <h3 className="mb-3 font-heading text-base font-semibold">Photos</h3>
        {needs_profile_photo && (
          <p
            className={
              'mb-3 rounded-md border px-3 py-2 text-sm ' +
              (has_profile_photo
                ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
                : 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400')
            }
            data-testid="profile-photo-requirement"
            data-satisfied={has_profile_photo}
          >
            {has_profile_photo
              ? 'Profile photo added.'
              : 'A profile photo of yourself is required — customers see it when they book you. Pick “Profile photo (your face)” above.'}
          </p>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-1 flex-col gap-2">
            <Label htmlFor="photo-type">
              Photo tag{' '}
              <span className="font-normal text-muted-foreground" data-testid="photo-tag-count">
                ({tag_count}/{MAX_PHOTOS_PER_TAG})
              </span>
            </Label>
            <SelectField
              id="photo-type"
              data-testid="photo-type"
              value={photo_type}
              onValueChange={(v) => set_photo_type(v as VendorPhotoType)}
              options={PHOTO_TYPES.map((t) => ({ value: t, label: PHOTO_TYPE_LABELS[t] ?? t }))}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => photo_input_ref.current?.click()}
            disabled={upload_photo_m.isPending || tag_room === 0}
            data-testid="photo-pick"
          >
            {upload_photo_m.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UploadCloud className="h-4 w-4" />
            )}
            Pick up to {tag_room} image{tag_room === 1 ? '' : 's'} (JPG/PNG/WEBP, ≤ 5 MB)
          </Button>
          <input
            ref={photo_input_ref}
            type="file"
            data-testid="photo-file-input"
            accept={PHOTO_ACCEPT}
            multiple
            onChange={handle_photo_select}
            className="hidden"
          />
        </div>
        {photos.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="photo-empty">
            No photos uploaded yet.
          </p>
        ) : (
          <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {photos.map((p) => (
              <li key={p.id} data-testid={`photo-uploaded-${p.id}`}>
                <button
                  type="button"
                  onClick={() => handle_view(`/vendor/uploads/photos/${p.id}/file?variant=full`)}
                  data-testid={`photo-view-${p.id}`}
                  className="group flex w-full flex-col overflow-hidden rounded-input border-2 border-border/60 bg-card text-left transition-shadow hover:shadow-clay"
                  aria-label={`View ${p.original_filename} full size`}
                >
                  <AuthImage
                    path={`/vendor/uploads/photos/${p.id}/file?variant=thumb`}
                    alt={p.original_filename}
                    className="aspect-square w-full object-cover"
                  />
                  <div className="flex items-center gap-1.5 p-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold">
                        {PHOTO_TYPE_LABELS[p.type] ?? p.type}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {p.original_filename}
                      </p>
                    </div>
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-xs text-muted-foreground">
        <XCircle className="mb-0.5 mr-1 inline-block h-3.5 w-3.5" />
        Uploads done from this screen aren't yet linked to your vendor profile — they're claimed
        when you hit Submit at the end.
      </p>
    </StepSection>
  );
}
